import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import css from './index.module.scss';
import Step1 from './comp/Step1';
import Step2 from './comp/Step2';
import Step3 from './comp/Step3';
import Success from './comp/Success';
import { ModalLeave, ModalLimit } from './comp/Modal';
import UserContent from './comp/UserContent';
import { useGlobal } from '@/utils/useGlobal';
import Crum from '@/components/Crum';
import { loginUser } from '@/utils/cookie';
import { CASE_SUBMIT_KEY } from '@/constants';
import dayjs from '@/utils/dayjs';
import { localRegionMap } from '@/utils/time';
import { signupCookie } from '@/utils/cookies/signupCookie';

export enum SUPPORT_CASE_TYPE {
    'Pre-sales' = '4',
    'Order' = '5',
    'Technical' = '6',
    'C&S' = '7',
}

export const supportMap: any = {
    [SUPPORT_CASE_TYPE['Pre-sales']]: 'Pre-sales support',
    [SUPPORT_CASE_TYPE['Order']]: 'Order support',
    [SUPPORT_CASE_TYPE['Technical']]: 'Technical Support',
    [SUPPORT_CASE_TYPE['C&S']]: 'Complaints and Suggestions',
};

export const supportOption = Object.keys(supportMap).map((key) => ({ value: key, label: supportMap[key] }));

export const ICON_DONE = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28" fill="none">
        <rect width="28" height="28" rx="14" fill="black" />
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M20.8992 9.27347C21.2452 9.63031 21.2364 10.2001 20.8796 10.5461L12.3869 18.7815C12.0378 19.12 11.4829 19.12 11.1338 18.7815L7.27347 15.0381C6.91663 14.6921 6.90787 14.1223 7.25389 13.7655C7.59991 13.4086 8.1697 13.3999 8.52653 13.7459L11.7603 16.8817L19.6265 9.25389C19.9833 8.90787 20.5531 8.91663 20.8992 9.27347Z"
            fill="white"
        />
    </svg>
);

export const ICON_MORE = () => (
    <svg className="icon-more" xmlns="http://www.w3.org/2000/svg" width="8" height="14" viewBox="0 0 8 14" fill="none">
        <path d="M0.999999 1L7 7L1 13" stroke="#707070" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const stepMap: any = {
    step1: {
        step1: 'show',
    },
    step2: {
        step1: 'show',
        step2: 'show',
    },
    step3: {
        step1: 'show',
        step2: 'show',
        step3: 'show',
    },
    success: {
        success: 'show',
    },
};

export default function GetSupport() {
    const { useLang, getSystemTime } = useGlobal();
    const { t } = useLang('supportcasecreate');
    const router = useRouter();
    const { locale = 'us' } = router;
    // 通过服务器时间 获取各国时间戳 更新提交次数
    const curDate = dayjs.tz(getSystemTime(), localRegionMap[locale] || localRegionMap.us).format('YYYY-MM-DD');
    const [user, setUser] = useState<any>({});
    const [openModalLeave, setOpenModalLeave] = useState(false);
    const [openModalLimit, setOpenModalLimit] = useState(false);
    const [currentData, setCurrentData] = useState<any>({});
    const [step, setStep] = useState<any>(stepMap.step1);
    const [caseData, setCaseData] = useState<any>({});

    /**
     * 回到上一步，提示未保存框，确认后，则清空当前步骤的数据
     */
    const onChangeStep = (step: string, data?: any) => {
        if (data) {
            setCurrentData({ step, data });
            setOpenModalLeave(true);
        } else {
            setStep(stepMap[step] || stepMap.step1);
        }
    };

    const onLeaveOk = () => {
        const { step, data } = currentData;
        setStep(stepMap[step] || stepMap.step1);
        if (data) {
            setCaseData(data);
        }
        setOpenModalLeave(false);
    };

    const onChangeCase = (data: any) => {
        setCaseData({ ...caseData, ...data });
    };

    function leaveTips(event: any) {
        /**
         * https://blog.csdn.net/Boale_H/article/details/118191577
         * 使用 react-router-dom
         *
         * TODO 多语言
         */
        const message = 'Your unsaved changes, are you sure you want to leave?';
        event.returnValue = message;
        return message;
    }

    useEffect(() => {
        const { user } = loginUser() || { user: {} };
        if (!('email' in user)) {
            signupCookie({ sector_name: 'Support Services', entry_position: 1 });
            router.push(`/${locale}/login?redirect=${encodeURIComponent(window.location.href)}`);
            return;
        }
        setUser(user);
        const currentSubmitTimes = JSON.parse(localStorage.getItem(CASE_SUBMIT_KEY) || '{}');
        if (currentSubmitTimes?.[curDate]?.[user.email] >= 3) {
            setOpenModalLimit(true);
            return;
        }
        window.addEventListener('beforeunload', leaveTips);
        return () => {
            window.removeEventListener('beforeunload', leaveTips);
        };
    }, []);

    useEffect(() => {
        // 提单成功后移除离开提示
        if (step.success) {
            window.removeEventListener('beforeunload', leaveTips);
        }
    }, [step]);

    return (
        <div className={`${css.GetSupport}`}>
            <Crum
                data={[{ name: t('crum_home'), link: '/' }, { name: t('crum_support'), link: '/support' }, { name: t('crum_support_create') }]}
                backgroundColor="#f0f1f2"
            />
            <div className="page-content">
                <p className="title">{t('title')}</p>
                <UserContent user={user} />
                <div className="form-content">
                    {step.step1 && <Step1 caseData={caseData} step={step} onChangeStep={onChangeStep} onChangeCase={onChangeCase} />}
                    {step.step2 && caseData?.caseTypeCode != SUPPORT_CASE_TYPE['C&S'] && (
                        <Step2 caseData={caseData} step={step} onChangeStep={onChangeStep} onChangeCase={onChangeCase} />
                    )}
                    {step.step3 && (
                        <Step3
                            caseData={caseData}
                            onChangeStep={onChangeStep}
                            onChangeCase={onChangeCase}
                            openModalLimit={() => setOpenModalLimit(true)}
                            user={user}
                            curDate={curDate}
                        />
                    )}
                    {step.success && <Success />}
                </div>
            </div>
            <ModalLeave open={openModalLeave} onCancel={() => setOpenModalLeave(false)} onOk={onLeaveOk} />
            <ModalLimit open={openModalLimit} onCancel={() => setOpenModalLimit(false)} />
            <style global jsx>
                {`
                    #allContentContainer {
                        min-height: unset;
                    }
                    .ant-message {
                        z-index: 999999;
                        .ant-message-notice .ant-message-notice-content {
                            border-radius: 6px;
                            overflow: hidden;
                        }
                    }
                `}
            </style>
        </div>
    );
}
